import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  IconButton,
  CircularProgress,
  Alert,
  Tooltip,
  Chip,
  Switch,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SmartToyOutlinedIcon from "@mui/icons-material/SmartToyOutlined";
import TuneOutlinedIcon from "@mui/icons-material/TuneOutlined";
import ReplyOutlinedIcon from "@mui/icons-material/ReplyOutlined";
import { ReactComponent as DropdownIcon } from "../../assets/images/icons/logs/dropdown-arrow.svg";
import ButtonOne from "../../common/normal-button";
import ButtonTwo from "../../common/dashboard-button";
import AppChip from "../../common/app-chip";
import AppDropdown from "../../common/app-dropdown";
import { ColorsConstants } from "../../cssUtils/constants";
import typography from "../../cssUtils/typography";
import { useAuth } from "../../hooks/AuthProvider";
import {
  NODEBOT_NODEBOTS,
  NODEBOT_CUSTOM_CHATBOTS,
  NODEBOT_FALLBACK_AUTORESPONDERS,
} from "../../config/urls";
import AppTable from "../../common/app-table";
import CustomTabs from "../../common/custom-tabs";
import StatTile from "../../common/stat-tile";
import TableSearchInput from "../TableSearchInput";
import WorkflowEdit from "./WorkflowEdit";
import FallbackAutoresponderEdit from "./FallbackAutoresponderEdit";
import CustomChatbotEdit from "./CustomChatbotEdit";
import LabelsCell from "./Components/LabelsCell";

const DEFAULT_ROWS_PER_PAGE = 10;

// Chip color per trigger type — also drives the legend
const TRIGGER_TYPE_META = {
  text: { label: "Text Keyword", bg: "#EEF2FF", color: "#4338CA" },
  template_button_reply: { label: "Template Button Reply", bg: "#ECFDF5", color: "#047857" },
};

const STATUS_FILTER_OPTIONS = [
  { value: "true", label: "Active" },
  { value: "false", label: "Inactive" },
];

// Fetch every row in one page — these lists are admin-curated (bots/autoresponders per
// tenant), not high-volume data, so a generous limit avoids DRF's default page size of
// 20 silently truncating the set that search/filter then operate on.
const LIST_FETCH_LIMIT = 200;

// Helper function to format date
const formatDate = (dateString) => {
  if (!dateString) return "--";
  try {
    const date = new Date(dateString);
    return date
      .toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      })
      .replace(/\//g, "/");
  } catch {
    return dateString;
  }
};

const Workflows = (props) => {
  const tenant = localStorage.getItem("tenant");
  const { getValidToken } = useAuth();

  // ── Shared search + status filter (applies across all sections/tables) ─────
  const [search, setSearch] = useState("");
  const [charCount, setCharCount] = useState(0);
  const [statusFilter, setStatusFilter] = useState(""); // "" | "true" | "false"

  const [workflows, setWorkflows] = useState([]);
  const [customChatbots, setCustomChatbots] = useState([]);
  const [fallbackAutoresponders, setFallbackAutoresponders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [alert, setAlert] = useState({ message: "", severity: "error" });

  // Activating a fallback autoresponder (only one may be active at a time)
  const [activatingId, setActivatingId] = useState(null);

  // Activating/deactivating a nodebot or custom chatbot from the list
  const [togglingId, setTogglingId] = useState(null);

  // Deleting a nodebot or custom chatbot from the list — `deleteTarget` holds the row
  // pending confirmation (or null), `deletingId` tracks the in-flight request.
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  // Saving a labels edit (any of the three resource types) from the list
  const [labelSavingId, setLabelSavingId] = useState(null);

  // Add/edit configuration drawer — replaces full-page navigation. Each panel stays
  // permanently mounted (see render below) so it has a "closed" (width: 0) state to
  // transition from/to; `activeDrawer` just picks which one is visible.
  const [activeDrawer, setActiveDrawer] = useState(null); // "workflow" | "custom_chatbot" | "fallback_autoresponder" | null
  const [workflowEditParams, setWorkflowEditParams] = useState(null);
  const [customChatbotEditParams, setCustomChatbotEditParams] = useState(null);
  const [autoresponderEditParams, setAutoresponderEditParams] = useState(null);

  const openEditDrawer = (type, params) => {
    if (type === "workflow") setWorkflowEditParams(params);
    else if (type === "custom_chatbot") setCustomChatbotEditParams(params);
    else if (type === "fallback_autoresponder") setAutoresponderEditParams(params);
    setActiveDrawer(type);
  };

  const closeEditDrawer = () => setActiveDrawer(null);

  // Whether the currently open edit drawer (whichever one it is — only one can be
  // open at a time) has unsaved changes. Reported by the drawers themselves via
  // `onDirtyChange`, see below.
  const [editDrawerDirty, setEditDrawerDirty] = useState(false);
  // Tab the user tried to switch to while the open drawer had unsaved changes —
  // holds the switch until they confirm via the dialog below.
  const [pendingTab, setPendingTab] = useState(null);

  // ── Tabs state ──────────────────────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState("chatbots");

  // Switching tabs while an edit drawer is open should always close it (so it
  // re-fetches fresh data next time it's opened, instead of showing whatever was
  // last loaded); if it has unsaved changes, confirm before discarding them.
  const handleTabChange = (_, value) => {
    if (value === activeTab) return;
    if (activeDrawer && editDrawerDirty) {
      setPendingTab(value);
      return;
    }
    if (activeDrawer) closeEditDrawer();
    setActiveTab(value);
  };

  const discardAndSwitchTab = () => {
    closeEditDrawer();
    setActiveTab(pendingTab);
    setPendingTab(null);
  };

  // ── Pagination state (Configuration Chatbots / Automations only — Custom
  // Chatbots and Fallback Autoresponders are expected to stay short lists) ────
  const [configPage, setConfigPage] = useState(0);
  const [configRowsPerPage, setConfigRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const [automationPage, setAutomationPage] = useState(0);
  const [automationRowsPerPage, setAutomationRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);

  const authHeaders = async () => {
    const token = await getValidToken();
    return {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
      "X-Tenant-Id": tenant,
    };
  };

  // Fetch all three lists on mount
  useEffect(() => {
    fetchAll();
  }, []);

  useEffect(() => {
    setConfigPage(0);
    setAutomationPage(0);
  }, [search, statusFilter]);

  const handleConfigPageChange = (_, newPage) => {
    setConfigPage(newPage);
  };

  const handleConfigRowsPerPageChange = (event) => {
    setConfigRowsPerPage(parseInt(event.target.value, 10));
    setConfigPage(0);
  };

  const handleAutomationPageChange = (_, newPage) => {
    setAutomationPage(newPage);
  };

  const handleAutomationRowsPerPageChange = (event) => {
    setAutomationRowsPerPage(parseInt(event.target.value, 10));
    setAutomationPage(0);
  };

  const fetchAll = async () => {
    setLoading(true);
    setAlert({ message: "", severity: "error" });

    try {
      const headers = await authHeaders();
      const [nodebotsRes, customChatbotsRes, autorespondersRes] = await Promise.all([
        fetch(`${NODEBOT_NODEBOTS}?limit=${LIST_FETCH_LIMIT}`, { headers }),
        fetch(`${NODEBOT_CUSTOM_CHATBOTS}?limit=${LIST_FETCH_LIMIT}`, { headers }),
        fetch(`${NODEBOT_FALLBACK_AUTORESPONDERS}?limit=${LIST_FETCH_LIMIT}`, { headers }),
      ]);

      if (!nodebotsRes.ok) throw new Error("Failed to fetch bots");
      if (!customChatbotsRes.ok) throw new Error("Failed to fetch custom chatbots");
      if (!autorespondersRes.ok) throw new Error("Failed to fetch fallback autoresponders");

      const nodebotsData = await nodebotsRes.json();
      const customChatbotsData = await customChatbotsRes.json();
      const autorespondersData = await autorespondersRes.json();

      setWorkflows(
        (nodebotsData.results || []).map((bot) => ({
          id: bot.id,
          resourceType: "nodebot",
          workflowName: bot.name || "Unnamed Bot",
          triggers: bot.triggers || [],
          triggerType: bot.trigger_type || "--",
          createdOn: formatDate(bot.created_at),
          updatedOn: formatDate(bot.updated_at),
          status: bot.is_active || false,
          recurringStartDate: bot.recurring_start_date,
          recurringEndDate: bot.recurring_end_date,
          useDateConstraints: bot.use_date_constraints || false,
          activeStartDate: bot.active_start_date,
          activeEndDate: bot.active_end_date,
          labels: bot.labels || [],
        })),
      );

      setCustomChatbots(
        (customChatbotsData.results || []).map((bot) => ({
          id: bot.id,
          resourceType: "custom_chatbot",
          workflowName: bot.name || "Unnamed Bot",
          triggers: bot.triggers || [],
          triggerType: "message",
          createdOn: formatDate(bot.created_at),
          updatedOn: formatDate(bot.updated_at),
          status: bot.is_active || false,
          useDateConstraints: bot.use_date_constraints || false,
          activeStartDate: bot.active_start_date,
          activeEndDate: bot.active_end_date,
          labels: bot.labels || [],
        })),
      );

      setFallbackAutoresponders(
        (autorespondersData.results || []).map((responder) => ({
          id: responder.id,
          resourceType: "fallback_autoresponder",
          name: responder.name || "Unnamed Autoresponder",
          message: responder.message || "",
          type: responder.type || "message",
          createdOn: formatDate(responder.created_at),
          updatedOn: formatDate(responder.updated_at),
          status: responder.is_active || false,
          labels: responder.labels || [],
        })),
      );
    } catch (err) {
      setAlert({
        message: err.message || "Error fetching workflows",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleRowClick = (workflow) => {
    if (workflow.resourceType !== "nodebot") return;

    props.onNavigate &&
      props.onNavigate("chatbot_flow", {
        workflowId: workflow.id,
        workflowName: workflow.workflowName,
        isActive: workflow.status,
        triggerType: workflow.triggerType,
        triggers: workflow.triggers,
        recurringStartDate: workflow.recurringStartDate,
        recurringEndDate: workflow.recurringEndDate,
      });
  };

  const handleEditClick = (e, workflow) => {
    e.stopPropagation();
    if (workflow.resourceType === "nodebot") {
      openEditDrawer("workflow", {
        workflowId: workflow.id,
        triggerType: workflow.triggerType,
      });
    } else if (workflow.resourceType === "custom_chatbot") {
      openEditDrawer("custom_chatbot", {
        customChatbotId: workflow.id,
        customChatbotName: workflow.workflowName,
      });
    }
  };

  const handleEditAutoresponderClick = (e, responder) => {
    e.stopPropagation();
    openEditDrawer("fallback_autoresponder", {
      autoresponderId: responder.id,
      autoresponderName: responder.name,
    });
  };

  // Toggle is_active for a nodebot or custom chatbot row via the dedicated
  // toggle-status endpoints (flip only — leaves triggers/dates untouched).
  const handleToggleStatus = async (e, row) => {
    e.stopPropagation();
    if (togglingId) return;

    const isCustomChatbot = row.resourceType === "custom_chatbot";
    const endpoint = isCustomChatbot ? NODEBOT_CUSTOM_CHATBOTS : NODEBOT_NODEBOTS;

    setTogglingId(row.id);
    try {
      const headers = await authHeaders();
      const response = await fetch(`${endpoint}${row.id}/toggle-status/`, {
        method: "POST",
        headers,
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data?.error || "Failed to update status");
      }

      const bot = await response.json();
      const applyToggle = (prev) =>
        prev.map((item) => (item.id === row.id ? { ...item, status: bot.is_active } : item));
      if (isCustomChatbot) setCustomChatbots(applyToggle);
      else setWorkflows(applyToggle);

      // Activating a bot whose "Any Message" trigger now shadows the active Fallback
      // Autoresponder (or vice versa) isn't blocked server-side, just flagged — surface
      // it here since this action was what triggered the overlap.
      if (bot.warnings?.length) {
        setAlert({ message: bot.warnings.join(" "), severity: "warning" });
      }
    } catch (err) {
      setAlert({ message: err.message || "Failed to update status", severity: "error" });
    } finally {
      setTogglingId(null);
    }
  };

  const handleDeleteClick = (e, row) => {
    e.stopPropagation();
    setDeleteTarget(row);
  };

  const handleCancelDelete = () => setDeleteTarget(null);

  const handleConfirmDelete = async () => {
    if (!deleteTarget || deletingId) return;
    const row = deleteTarget;
    const isCustomChatbot = row.resourceType === "custom_chatbot";
    const endpoint = isCustomChatbot ? NODEBOT_CUSTOM_CHATBOTS : NODEBOT_NODEBOTS;

    setDeletingId(row.id);
    try {
      const headers = await authHeaders();
      const response = await fetch(`${endpoint}${row.id}/delete/`, {
        method: "DELETE",
        headers,
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data?.error || "Failed to delete");
      }

      const removeRow = (prev) => prev.filter((item) => item.id !== row.id);
      if (isCustomChatbot) setCustomChatbots(removeRow);
      else setWorkflows(removeRow);

      setDeleteTarget(null);
    } catch (err) {
      setAlert({ message: err.message || "Failed to delete", severity: "error" });
    } finally {
      setDeletingId(null);
    }
  };

  // Persist a labels edit for any of the three resource types, made inline from the
  // list table (see LabelsCell) — always sends the full next label list.
  const labelsEndpointFor = (resourceType) => {
    switch (resourceType) {
      case "custom_chatbot":
        return NODEBOT_CUSTOM_CHATBOTS;
      case "fallback_autoresponder":
        return NODEBOT_FALLBACK_AUTORESPONDERS;
      default:
        return NODEBOT_NODEBOTS;
    }
  };

  const setLocalLabels = (resourceType, id, labels) => {
    const apply = (prev) => prev.map((item) => (item.id === id ? { ...item, labels } : item));
    if (resourceType === "custom_chatbot") setCustomChatbots(apply);
    else if (resourceType === "fallback_autoresponder") setFallbackAutoresponders(apply);
    else setWorkflows(apply);
  };

  const handleLabelsChange = async (row, newLabels) => {
    if (labelSavingId) return;

    setLabelSavingId(row.id);
    try {
      const headers = await authHeaders();
      const response = await fetch(`${labelsEndpointFor(row.resourceType)}${row.id}/labels/`, {
        method: "PATCH",
        headers,
        body: JSON.stringify({ labels: newLabels }),
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data?.error || "Failed to update labels");
      }

      const updated = await response.json();
      setLocalLabels(row.resourceType, row.id, updated.labels || []);
    } catch (err) {
      setAlert({ message: err.message || "Failed to update labels", severity: "error" });
    } finally {
      setLabelSavingId(null);
    }
  };

  const handleActivateAutoresponder = async (e, responder) => {
    e.stopPropagation();
    if (responder.status || activatingId) return;

    setActivatingId(responder.id);
    try {
      const headers = await authHeaders();
      const response = await fetch(`${NODEBOT_FALLBACK_AUTORESPONDERS}${responder.id}/activate/`, {
        method: "POST",
        headers,
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data?.error || "Failed to activate autoresponder");
      }

      const data = await response.json();
      setFallbackAutoresponders((prev) =>
        prev.map((row) => ({ ...row, status: row.id === responder.id })),
      );

      // Doesn't block activation, but a bot with an active "Any Message" trigger would
      // shadow this autoresponder entirely (it matches every message first) — flag it.
      if (data.warnings?.length) {
        setAlert({ message: data.warnings.join(" "), severity: "warning" });
      }
    } catch (err) {
      setAlert({ message: err.message || "Failed to activate autoresponder", severity: "error" });
    } finally {
      setActivatingId(null);
    }
  };

  const handleSearchChange = (value) => {
    if (value.length > 50) return;
    setSearch(value);
    setCharCount(value.length);
  };

  const matchesStatus = (row) =>
    statusFilter === "" || row.status === (statusFilter === "true");

  const matchesSearch = (row) => {
    const query = search.toLowerCase();
    if (!query) return true;
    if (row.workflowName?.toLowerCase().includes(query)) return true;
    if (row.triggers?.some((t) => t.value?.toLowerCase().includes(query))) return true;
    if (row.name?.toLowerCase().includes(query)) return true;
    if (row.message?.toLowerCase().includes(query)) return true;
    return false;
  };

  const filtered = workflows.filter((wf) => matchesSearch(wf) && matchesStatus(wf));

  // Automations = recurring nodebots. Chatbots = everything else.
  const automationRows = filtered.filter((wf) => wf.triggerType === "recurring");
  const configurationChatbotRows = filtered.filter((wf) => wf.triggerType !== "recurring");

  const customChatbotRows = customChatbots.filter((bot) => matchesSearch(bot) && matchesStatus(bot));

  const fallbackAutoresponderRows = fallbackAutoresponders.filter(
    (responder) => matchesSearch(responder) && matchesStatus(responder),
  );

  const nameColumnLabel = activeTab === "automations" ? "Automation Name" : "Bot Name";

  const columns = [
    {
      id: "workflowName",
      label: nameColumnLabel,
      minWidth: 200,
      render: (row) => (
        <Box>
          <Typography
            sx={{
              ...typography.bodySmall,
              fontWeight: 500,
              color: ColorsConstants.primaryBackgroundColor,
            }}
          >
            {row.workflowName}
          </Typography>
          {row.triggerType === "recurring" && row.status && (
            <Typography
              sx={{
                ...typography.bodySmall,
                color: ColorsConstants.primaryGray40,
                mt: 0.5,
                fontStyle: "italic",
              }}
            >
              {row.recurringStartDate} to {row.recurringEndDate}
            </Typography>
          )}
          {row.triggerType !== "recurring" && row.useDateConstraints && row.activeStartDate && (
            <Typography
              sx={{
                ...typography.bodySmall,
                color: ColorsConstants.primaryGray40,
                mt: 0.5,
                fontStyle: "italic",
              }}
            >
              {row.activeStartDate} to {row.activeEndDate}
            </Typography>
          )}
        </Box>
      ),
    },
    {
      id: "triggers",
      label: "Triggers",
      minWidth: 260,
      render: (row) => {
        if (!row.triggers || row.triggers.length === 0) {
          return (
            <Typography sx={{ ...typography.bodySmall, color: "#04142F" }}>--</Typography>
          );
        }
        return (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
            {row.triggers.map((entry, idx) => {
              const meta = TRIGGER_TYPE_META[entry.type];
              // Any Message / Any Text carry a fixed internal sentinel as their
              // `value` (see Trigger.ANY_MESSAGE_VALUE/ANY_TEXT_VALUE in
              // nodebot/models.py) — never show that raw value to the user.
              const label =
                entry.type === "any_message"
                  ? "Any Message"
                  : entry.type === "text" && entry.match_mode === "any"
                    ? "Any Text"
                    : entry.value;
              return (
                <AppChip
                  key={idx}
                  label={label}
                  sx={{ backgroundColor: meta?.bg || "#F3F4F6", color: meta?.color || "#374151" }}
                />
              );
            })}
          </Box>
        );
      },
    },
    {
      id: "labels",
      label: "Labels",
      minWidth: 220,
      render: (row) => (
        <LabelsCell
          labels={row.labels || []}
          saving={labelSavingId === row.id}
          onChange={(newLabels) => handleLabelsChange(row, newLabels)}
        />
      ),
    },
    {
      id: "status",
      label: "Status",
      minWidth: 110,
      render: (row) => (
        <Box onClick={(e) => e.stopPropagation()} sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          {togglingId === row.id ? (
            <CircularProgress size={16} sx={{ mx: 1.25 }} />
          ) : (
            <Tooltip title={row.status ? "Deactivate" : "Activate"} placement="top" arrow>
              <Switch
                size="small"
                checked={row.status}
                onChange={(e) => handleToggleStatus(e, row)}
                sx={{
                  "& .MuiSwitch-switchBase.Mui-checked": { color: ColorsConstants.primaryBlue100 },
                  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                    backgroundColor: ColorsConstants.primaryBlue100,
                  },
                }}
              />
            </Tooltip>
          )}
          <Typography
            sx={{
              ...typography.bodySmall,
              fontWeight: 500,
              color: row.status ? "#2E7D32" : "#6C7C93",
            }}
          >
            {row.status ? "On" : "Off"}
          </Typography>
        </Box>
      ),
    },
    {
      id: "dates",
      label: "Created/Updated",
      minWidth: 180,
      render: (row) => (
        <Box>
          <Typography sx={{ ...typography.bodySmall, color: "#04142F" }}>
            Created: {row.createdOn}
          </Typography>
          <Typography sx={{ ...typography.bodySmall, color: "#6C7C93", mt: 0.25 }}>
            Updated: {row.updatedOn}
          </Typography>
        </Box>
      ),
    },
    {
      id: "actions",
      label: "Actions",
      minWidth: 100,
      render: (row) => {
        const isNodebot = row.resourceType === "nodebot";
        const isCustomChatbot = row.resourceType === "custom_chatbot";
        return (
          <Box onClick={(e) => e.stopPropagation()} sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Tooltip title="Edit" placement="top" arrow>
              <span>
                <IconButton
                  size="small"
                  onClick={(e) => handleEditClick(e, row)}
                  disabled={!isNodebot && !isCustomChatbot}
                  sx={{
                    color: "#0F172A",
                    borderRadius: "6px",
                    "&:hover": { bgcolor: "#F0F2F8", color: "#0F172A" },
                  }}
                >
                  <EditIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Delete" placement="top" arrow>
              <span>
                <IconButton
                  size="small"
                  onClick={(e) => handleDeleteClick(e, row)}
                  disabled={!isNodebot && !isCustomChatbot}
                  sx={{
                    color: "#0F172A",
                    borderRadius: "6px",
                    "&:hover": { bgcolor: "#FFF0F0", color: "#E05252" },
                  }}
                >
                  <DeleteIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        );
      },
    },
  ];

  // Automations are always "recurring" with no trigger keywords, so those columns are redundant.
  const automationColumns = columns.filter(
    (col) => col.id !== "triggerValues" && col.id !== "triggerType",
  );

  const autoresponderColumns = [
    {
      id: "name",
      label: "Name",
      minWidth: 200,
      render: (row) => (
        <Typography
          sx={{
            ...typography.bodySmall,
            fontWeight: 500,
            color: ColorsConstants.primaryBackgroundColor,
          }}
        >
          {row.name}
        </Typography>
      ),
    },
    {
      id: "message",
      label: "Message",
      minWidth: 320,
      render: (row) => (
        <Typography
          sx={{
            ...typography.bodySmall,
            color: "#04142F",
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >
          {row.type === "live_agent" ? "--" : row.message || "--"}
        </Typography>
      ),
    },
    {
      id: "type",
      label: "Type",
      minWidth: 110,
      render: (row) => (
        <AppChip
          label={row.type === "live_agent" ? "Live Agent" : "Message"}
          sx={
            row.type === "live_agent"
              ? { backgroundColor: "#EEF2FF", color: "#4338CA" }
              : { backgroundColor: "#F5F5F5", color: "#6C7C93" }
          }
        />
      ),
    },
    {
      id: "labels",
      label: "Labels",
      minWidth: 220,
      render: (row) => (
        <LabelsCell
          labels={row.labels || []}
          saving={labelSavingId === row.id}
          onChange={(newLabels) => handleLabelsChange(row, newLabels)}
        />
      ),
    },
    {
      id: "status",
      label: "Status",
      minWidth: 110,
      render: (row) => (
        <AppChip
          label={row.status ? "Active" : "Inactive"}
          sx={
            row.status
              ? { backgroundColor: "#E6F4EA", color: "#2E7D32" }
              : { backgroundColor: "#F5F5F5", color: "#6C7C93" }
          }
        />
      ),
    },
    {
      id: "dates",
      label: "Created/Updated",
      minWidth: 180,
      render: (row) => (
        <Box>
          <Typography sx={{ ...typography.bodySmall, color: "#04142F" }}>
            Created: {row.createdOn}
          </Typography>
          <Typography sx={{ ...typography.bodySmall, color: "#6C7C93", mt: 0.25 }}>
            Updated: {row.updatedOn}
          </Typography>
        </Box>
      ),
    },
    {
      id: "actions",
      label: "Actions",
      minWidth: 80,
      render: (row) => (
        <Box onClick={(e) => e.stopPropagation()} sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Tooltip title="Edit" placement="top" arrow>
            <span>
              <IconButton
                size="small"
                onClick={(e) => handleEditAutoresponderClick(e, row)}
                sx={{
                  color: "#0F172A",
                  borderRadius: "6px",
                  "&:hover": { bgcolor: "#F0F2F8", color: "#0F172A" },
                }}
              >
                <EditIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      ),
    },
  ];

  const rowSx = (row, index) => ({
    backgroundColor: index % 2 === 0 ? "#F0F5FF" : "#FFFFFF",
    cursor: row.resourceType === "nodebot" || !row.resourceType ? "pointer" : "default",
    "&:hover": {
      backgroundColor: index % 2 === 0 ? "#E3F0FF" : "#E6F0FF",
    },
    height: "47px",
  });

  const tableContainerSx = {
    scrollbarWidth: "thin",
    scrollbarColor: "#C5CEDB #F3F6FB",
    "&::-webkit-scrollbar": { width: "8px" },
    "&::-webkit-scrollbar-track": { backgroundColor: "#F3F6FB", borderRadius: "8px" },
    "&::-webkit-scrollbar-thumb": { backgroundColor: "#C5CEDB", borderRadius: "8px" },
    "&::-webkit-scrollbar-thumb:hover": { backgroundColor: "#A8B4C7" },
  };

  const countSummary = (rows) => ({
    active: rows.filter((row) => row.status).length,
    total: rows.length,
  });

  const renderCountBadges = (rows) => {
    const { active, total } = countSummary(rows);
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
        <AppChip label={`${active} active`} sx={{ backgroundColor: "#E6F4EA", color: "#2E7D32" }} />
        <AppChip label={`${total} total`} sx={{ backgroundColor: "#F3F4F6", color: "#374151" }} />
      </Box>
    );
  };

  const renderSectionHeader = (icon, title, description, rows, action = null) => (
    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, mb: 3 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: 36,
          height: 36,
          borderRadius: "8px",
          backgroundColor: "#F0F5FF",
          color: ColorsConstants.primaryBlue100,
          flexShrink: 0,
        }}
      >
        {icon}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, flexWrap: "wrap" }}>
          <Typography sx={{ ...typography.h2, color: "#1D2330" }}>{title}</Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            {renderCountBadges(rows)}
            {action}
          </Box>
        </Box>
        <Typography sx={{ ...typography.bodySmall, color: "#6C7C93", mt: 0.25 }}>
          {description}
        </Typography>
      </Box>
    </Box>
  );

  // Show loading state
  if (loading) {
    return (
      <Box
        sx={{
          height: "80vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexDirection: "column",
          gap: 2,
        }}
      >
        <CircularProgress />
        <Typography sx={{ ...typography.bodyLarge, color: ColorsConstants.primaryGray40 }}>
          Loading workflows...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", alignItems: "stretch", minHeight: "100vh" }}>
    <Box sx={{ flex: 1, minWidth: 0, backgroundColor: "#FAFAFA", p: 3 }}>
      {/* Page Title */}
      <Box sx={{ mb: 2.5 }}>
        <Typography sx={{ ...typography.h1, color: "#1D2330" }}>Workflows</Typography>
        <Typography sx={{ ...typography.bodySmall, color: "#6C7C93", mt: 0.25 }}>
          Build multi-step chat flows
        </Typography>
      </Box>

      {/* Alert Message */}
      {alert.message && (
        <Box sx={{ mb: 2 }}>
          <Alert
            severity={alert.severity}
            onClose={() => setAlert({ message: "", severity: "error" })}
            sx={{ borderRadius: "8px" }}
          >
            {alert.message}
          </Alert>
        </Box>
      )}

      {/* Tabs */}
      <Box sx={{ mb: 2 }}>
        <CustomTabs
          value={activeTab}
          onChange={handleTabChange}
          tabs={[
            { value: "chatbots", label: "Chatbots" },
            { value: "automations", label: "Automations" },
          ]}
          sx={{ maxWidth: 360 }}
        />
      </Box>

      {activeTab === "chatbots" && (
        <>
          {(() => {
            const allChatbotRows = [...customChatbotRows, ...configurationChatbotRows, ...fallbackAutoresponderRows];
            const { active, total } = countSummary(allChatbotRows);
            return (
              <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", mb: 2 }}>
                <StatTile value={total} label="Total chatbots" valueColor={ColorsConstants.primaryBlue100} />
                <StatTile value={active} label="Active" valueColor="#2E7D32" />
                <StatTile value={total - active} label="Inactive" valueColor="#EA580C" />
              </Box>
            );
          })()}

          {/* Trigger type legend */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
            {Object.values(TRIGGER_TYPE_META).map((meta) => (
              <Box key={meta.label} sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                <Box sx={{ width: 10, height: 10, borderRadius: "50%", backgroundColor: meta.color }} />
                <Typography sx={{ ...typography.bodySmall, color: "#6C7C93" }}>{meta.label}</Typography>
              </Box>
            ))}
          </Box>
        </>
      )}

      {/* Search Bar + Active/Inactive filter — shared across all tables below */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 3 }}>
        <Box sx={{ flex: 1, minWidth: 200 }}>
          <TableSearchInput
            searchQuery={search}
            onSearchChange={(e) => handleSearchChange(e.target.value)}
            charCount={charCount}
            tooltip="Search by name, trigger, or message across Custom Chatbots, Configuration Chatbots and Fallback Autoresponders"
            placeholder="Search by name, trigger, or message"
          />
        </Box>
        <AppDropdown
          endIcon={<DropdownIcon />}
          menuItems={STATUS_FILTER_OPTIONS}
          selectedValue={statusFilter}
          onMenuSelect={(value) => setStatusFilter(statusFilter === value ? "" : value)}
          sx={{ whiteSpace: "nowrap", minWidth: 130, px: 1.2 }}
        >
          Status
          {statusFilter && (
            <Chip
              label={STATUS_FILTER_OPTIONS.find((o) => o.value === statusFilter)?.label}
              size="small"
              onDelete={(e) => {
                e.stopPropagation();
                setStatusFilter("");
              }}
              sx={{ ml: 1 }}
            />
          )}
        </AppDropdown>
        {statusFilter && (
          <ButtonOne
            variant="outlined"
            onClick={() => setStatusFilter("")}
            sx={{
              height: 36,
              px: 2,
              ...typography.bodyLarge,
              border: "1px solid #DCDFE5",
              color: "#6C7C93",
              "&:hover": { border: "1px solid #9CA3AF", backgroundColor: "transparent" },
            }}
          >
            Reset
          </ButtonOne>
        )}
      </Box>

      {activeTab === "chatbots" ? (
        <>
          {renderSectionHeader(
            <SmartToyOutlinedIcon sx={{ fontSize: 20 }} />,
            "Custom Chatbots",
            "Trigger-gated hardcoded chatbot logic, managed outside the flow builder.",
            customChatbotRows,
          )}
          <AppTable
            columns={columns}
            rows={customChatbotRows}
            rowKey="id"
            onRowClick={handleRowClick}
            rowClickable={false}
            maxHeight={500}
            containerSx={tableContainerSx}
            rowSx={rowSx}
            paperSx={{ border: "none", boxShadow: "none", mb: 6 }}
            emptyMessage="No custom chatbots found"
          />

          {renderSectionHeader(
            <TuneOutlinedIcon sx={{ fontSize: 20 }} />,
            "Configuration Chatbots",
            "Chatbots you've built and configured using the flow builder.",
            configurationChatbotRows,
          )}
          <AppTable
            columns={columns}
            rows={configurationChatbotRows}
            rowKey="id"
            onRowClick={handleRowClick}
            rowClickable={true}
            maxHeight={500}
            containerSx={tableContainerSx}
            rowSx={rowSx}
            paperSx={{ border: "none", boxShadow: "none", mb: 6 }}
            emptyMessage="No configuration chatbots found"
            count={configurationChatbotRows.length}
            page={configPage}
            rowsPerPage={configRowsPerPage}
            rowsPerPageOptions={[20, 50, 100]}
            onPageChange={handleConfigPageChange}
            onRowsPerPageChange={handleConfigRowsPerPageChange}
          />

          {renderSectionHeader(
            <ReplyOutlinedIcon sx={{ fontSize: 20 }} />,
            "Fallback Autoresponders",
            "Static catch-all replies for messages that match no bot trigger — only one may be active at a time.",
            fallbackAutoresponderRows,
          )}
          <AppTable
            columns={autoresponderColumns}
            rows={fallbackAutoresponderRows}
            rowKey="id"
            rowClickable={false}
            maxHeight={500}
            containerSx={tableContainerSx}
            rowSx={rowSx}
            paperSx={{ border: "none", boxShadow: "none", mb: 0 }}
            emptyMessage="No fallback autoresponders found"
          />
        </>
      ) : (
        <>
          <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1.5 }}>
            {renderCountBadges(automationRows)}
          </Box>
          <AppTable
            columns={automationColumns}
            rows={automationRows}
            rowKey="id"
            onRowClick={handleRowClick}
            rowClickable={true}
            maxHeight={500}
            containerSx={tableContainerSx}
            rowSx={rowSx}
            paperSx={{ border: "none", boxShadow: "none", mb: 0 }}
            emptyMessage="No automations found"
            count={automationRows.length}
            page={automationPage}
            rowsPerPage={automationRowsPerPage}
            rowsPerPageOptions={[20, 50, 100]}
            onPageChange={handleAutomationPageChange}
            onRowsPerPageChange={handleAutomationRowsPerPageChange}
          />
        </>
      )}

    </Box>

      {/* Add/Edit Configuration Drawer — permanently mounted (each starts at width: 0)
          so the very first open actually slides in instead of popping into place. */}
      <WorkflowEdit
        open={activeDrawer === "workflow"}
        pageParams={workflowEditParams}
        onClose={closeEditDrawer}
        onSaved={fetchAll}
        onDirtyChange={setEditDrawerDirty}
      />
      <CustomChatbotEdit
        open={activeDrawer === "custom_chatbot"}
        pageParams={customChatbotEditParams}
        onClose={closeEditDrawer}
        onSaved={fetchAll}
        onDirtyChange={setEditDrawerDirty}
      />
      <FallbackAutoresponderEdit
        open={activeDrawer === "fallback_autoresponder"}
        pageParams={autoresponderEditParams}
        onClose={closeEditDrawer}
        onSaved={fetchAll}
        onDirtyChange={setEditDrawerDirty}
      />

      {/* Confirms discarding unsaved edits before switching the Chatbots/Automations tab */}
      <Dialog
        open={!!pendingTab}
        onClose={(e, reason) => {
          if (reason === "backdropClick") return;
          setPendingTab(null);
        }}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: "16px", p: 1 } }}
      >
        <DialogTitle sx={{ ...typography.h1 }}>Unsaved changes</DialogTitle>
        <DialogContent>
          <Typography sx={{ ...typography.bodyLarge, color: "#505050" }}>
            You have unsaved changes in this editor. Switching tabs will discard them.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <ButtonTwo onClick={() => setPendingTab(null)} variant="outlined">
            Keep Editing
          </ButtonTwo>
          <ButtonOne
            onClick={discardAndSwitchTab}
            variant="contained"
            sx={{
              backgroundColor: "#E05252",
              "&:hover": { backgroundColor: "#E05252", opacity: 0.9 },
            }}
          >
            Discard &amp; Switch
          </ButtonOne>
        </DialogActions>
      </Dialog>

      {/* Confirms deleting a nodebot/custom chatbot before it happens — irreversible */}
      <Dialog
        open={!!deleteTarget}
        onClose={(e, reason) => {
          if (reason === "backdropClick" || deletingId) return;
          handleCancelDelete();
        }}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: "16px", p: 1 } }}
      >
        <DialogTitle sx={{ ...typography.h1 }}>Delete {deleteTarget?.workflowName}?</DialogTitle>
        <DialogContent>
          <Typography sx={{ ...typography.bodyLarge, color: "#505050" }}>
            This will permanently delete this{" "}
            {deleteTarget?.resourceType === "custom_chatbot" ? "custom chatbot" : "bot"} and its
            triggers. This action can't be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <ButtonTwo onClick={handleCancelDelete} variant="outlined" disabled={!!deletingId}>
            Cancel
          </ButtonTwo>
          <ButtonOne
            onClick={handleConfirmDelete}
            variant="contained"
            disabled={!!deletingId}
            sx={{
              backgroundColor: "#E05252",
              "&:hover": { backgroundColor: "#E05252", opacity: 0.9 },
            }}
          >
            {deletingId ? <CircularProgress size={16} sx={{ color: "#FFFFFF" }} /> : "Delete"}
          </ButtonOne>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Workflows;
